import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  Signal,
  signal,
  WritableSignal
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { OrAvatarComponent, OrIconComponent, PageLayoutComponent, TitileNavbarComponent } from '@ui-components';
import { ActivatedRoute, ParamMap, Router, RouterLink } from '@angular/router';
import { map, Observable, shareReplay, switchMap, take, tap } from 'rxjs';
import { NgTemplateOutlet } from '@angular/common';
import { IonicModule } from '@ionic/angular';
import { NotificationViewModel } from '../../application/interfaces';
import {
  AppRoutes,
  GlobalRefreshService,
  MODAL_TOKEN,
  ModalProvider,
  NOTIFICATION_TOKEN,
  NotificationPermissionProvider,
  NotificationsRoutes,
  PLATFORM_TOKEN,
  PlatformProvider
} from '@core';
import { AndroidSettings, IOSSettings, NativeSettings } from 'capacitor-native-settings';
import { notificationRedirectUtil } from '../../application/utils';
import {
  GetAlertsSettingsQueryHandler,
  GetAlertsSettingsQueryResult,
  MarkAsReadNotificationsCommandHandler,
  MarkNotificationAsClickedCommandHandler,
  NotificationsListQueryHandler
} from '../../application/handlers';
import { NotificationsTabsResult } from '../../application/handlers/query/notifications-list/notifications-list.query-handler';
import { NotificationType } from '../../application/enums';

export enum NotificationTab {
  Notifications = 'notifications',
  Alerts = 'alerts'
}

@Component({
  selector: 'lib-notifications-list',
  templateUrl: './notifications-list.component.html',
  styleUrls: ['./notifications-list.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    TitileNavbarComponent,
    IonicModule,
    OrAvatarComponent,
    NgTemplateOutlet,
    PageLayoutComponent,
    RouterLink,
    OrIconComponent
  ],
  providers: [MarkNotificationAsClickedCommandHandler, GetAlertsSettingsQueryHandler]
})
export class NotificationsListComponent {
  private readonly notificationsListQueryHandler: NotificationsListQueryHandler = inject(NotificationsListQueryHandler);
  private readonly markAsReadNotificationsCommandHandler: MarkAsReadNotificationsCommandHandler = inject(
    MarkAsReadNotificationsCommandHandler
  );
  private readonly router: Router = inject(Router);
  private readonly route: ActivatedRoute = inject(ActivatedRoute);
  private readonly modalProvider: ModalProvider = inject(MODAL_TOKEN);
  private readonly platformProvider: PlatformProvider = inject(PLATFORM_TOKEN);
  private readonly notificationPermissionProvider: NotificationPermissionProvider = inject(NOTIFICATION_TOKEN);
  private readonly globalRefreshService: GlobalRefreshService = inject(GlobalRefreshService);
  private readonly markNotificationAsClickedCommandHandler: MarkNotificationAsClickedCommandHandler = inject(
    MarkNotificationAsClickedCommandHandler
  );
  private readonly getAlertsSettingsQueryHandler: GetAlertsSettingsQueryHandler = inject(GetAlertsSettingsQueryHandler);

  private readonly TAB_QUERY_PARAM: string = 'tab';

  readonly specificNotificationImage: NotificationType[] = [
    NotificationType.NewTrophyReceived,
    NotificationType.UserPenaltyReceived
  ];

  readonly notificationTab: typeof NotificationTab = NotificationTab;

  private readonly isNotificationPermissionsEnabled$: Observable<boolean> = this.globalRefreshService.refresh$.pipe(
    switchMap(() => this.notificationPermissionProvider.isEnabled())
  );

  readonly isNotificationPermissionsEnabled: Signal<boolean | undefined> = toSignal(
    this.isNotificationPermissionsEnabled$
  );

  readonly isPageLoaded: WritableSignal<boolean> = signal<boolean>(false);

  readonly customBackUrl: Signal<string> = signal<string>(`/${AppRoutes.DASHBOARD}`);

  readonly activeTab: Signal<NotificationTab> = toSignal(
    this.route.queryParamMap.pipe(
      map((params: ParamMap) =>
        params.get(this.TAB_QUERY_PARAM) === NotificationTab.Alerts
          ? NotificationTab.Alerts
          : NotificationTab.Notifications
      )
    ),
    { initialValue: NotificationTab.Notifications }
  );

  readonly settingsUrl: Signal<string> = computed(() =>
    this.activeTab() === NotificationTab.Notifications
      ? `/${AppRoutes.NOTIFICATIONS}/${NotificationsRoutes.PREFERENCES}`
      : `/${AppRoutes.NOTIFICATIONS}/${NotificationsRoutes.ALERTS}`
  );

  readonly settingsLabel: Signal<string> = computed(() =>
    this.activeTab() === NotificationTab.Notifications ? 'Ustawienia powiadomień' : 'Ustawienia alertów'
  );

  readonly emptyNewListLabel: Signal<string> = computed(() =>
    this.activeTab() === NotificationTab.Alerts ? 'Brak nowych alertów' : 'Brak nowych powiadomień'
  );

  private readonly markedTabs: Set<NotificationTab> = new Set();

  private readonly tabs$: Observable<NotificationsTabsResult> = this.modalProvider.showLoading$().pipe(
    switchMap(() => this.notificationsListQueryHandler.getNotificationsTabs()),
    take(1),
    tap(() => this.isPageLoaded.set(true)),
    tap(() => this.modalProvider.dismissLoading$()),
    shareReplay(1)
  );

  readonly tabs: Signal<NotificationsTabsResult | undefined> = toSignal(this.tabs$);

  readonly notificationsUnreadCount: Signal<number> = computed(
    () => this.tabs()?.notifications.filter((n: NotificationViewModel) => !n.isRead).length ?? 0
  );

  readonly alertsUnreadCount: Signal<number> = computed(
    () => this.tabs()?.alerts.filter((n: NotificationViewModel) => !n.isRead).length ?? 0
  );

  private readonly activeNotifications: Signal<NotificationViewModel[]> = computed(() => {
    const data: NotificationsTabsResult | undefined = this.tabs();
    if (!data) return [];
    return this.activeTab() === NotificationTab.Notifications ? data.notifications : data.alerts;
  });

  readonly newNotifications: Signal<NotificationViewModel[]> = computed(() =>
    this.activeNotifications().filter((n: NotificationViewModel) => !n.isRead)
  );

  readonly pastNotifications: Signal<NotificationViewModel[]> = computed(() =>
    this.activeNotifications().filter((n: NotificationViewModel) => n.isRead)
  );

  private readonly alertsSettings: Signal<GetAlertsSettingsQueryResult | undefined> = toSignal(
    this.getAlertsSettingsQueryHandler.getAlertsSettings().pipe(take(1))
  );

  readonly isAlertsDisabledInfoVisible: Signal<boolean> = computed(() => {
    const settings: GetAlertsSettingsQueryResult | undefined = this.alertsSettings();

    return this.activeTab() === NotificationTab.Alerts && !!settings && !settings.isEnabled;
  });

  constructor() {
    this.registerActiveTabAutoMarkAsRead();
  }

  setActiveTab(tab: NotificationTab): void {
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { [this.TAB_QUERY_PARAM]: tab },
      queryParamsHandling: 'merge',
      replaceUrl: true
    });
  }

  openNotificationPermissions(): Promise<{ status: boolean }> {
    if (this.platformProvider.isAndroid) {
      return NativeSettings.openAndroid({ option: AndroidSettings.AppNotification });
    }
    return NativeSettings.openIOS({ option: IOSSettings.Notifications });
  }

  redirect(notification: NotificationViewModel): void {
    this.markNotificationAsClickedCommandHandler.markAsClicked(notification.id).pipe(take(1)).subscribe();

    notificationRedirectUtil({
      type: notification.type,
      gameId: notification.gameId,
      router: this.router,
      customId: notification.customId,
      customRedirectUrl: notification.customRedirectUrl,
      teamId: notification.teamId,
      receiverUserId: notification.receiverUserId,
      customType: notification.customType,
      onCustomModalOpen: () => this.openCustomNotificationModal,
      modalProvider: this.modalProvider
    });
  }

  private openCustomNotificationModal(): void {}

  private registerActiveTabAutoMarkAsRead(): void {
    effect(() => {
      const data: NotificationsTabsResult | undefined = this.tabs();
      if (!data) return;

      const tab: NotificationTab = this.activeTab();
      if (this.markedTabs.has(tab)) return;
      this.markedTabs.add(tab);

      const list: NotificationViewModel[] = tab === NotificationTab.Notifications ? data.notifications : data.alerts;
      const unreadIds: string[] = list
        .filter((n: NotificationViewModel) => !n.isRead)
        .map((n: NotificationViewModel) => n.id);

      if (!unreadIds.length) return;

      this.markAsReadNotificationsCommandHandler.markAsRead(unreadIds).pipe(take(1)).subscribe();
    });
  }
}
